/**
 * Kickbacks (COL-621): a month's rent lowered for a tenant at coliviq's cost —
 * a reward the tenant earned, e.g. EUR 100 for finding the next tenant. The
 * owner is not the one who pays for it.
 *
 * A kickback lives on the rent row itself, in the two columns referral credits
 * already use (COL-434): `amount_cents` is what the TENANT is charged, and
 * `gross_amount_cents` keeps the full rent — what the OWNER earned. A referral
 * credit is a kickback too, and reads as one everywhere.
 *
 * Everything on the owner's side counts the gross figure: the owner payout,
 * the collected-based fee on an offset invoice (a pct_of_rent fee is charged on
 * the full rent) and the owner portal (which reads the plain COALESCE form). Everything on the tenant's side —
 * reminders, dunning, SEPA, "is this row paid" — keeps reading `amount_cents`.
 * The SQL twin is offset_invoice_live_fee's `owner_cents`
 * (kickback_offset_live_fee_counts_gross_rent).
 */

export type KickbackRentRow = {
  amount_cents?: number | null;
  gross_amount_cents?: number | null;
};

/**
 * What the owner earned on a rent row: its gross rent, never below what the
 * tenant is charged. GREATEST rather than a bare COALESCE because the rent
 * repricers (a rent change, a room reassignment) rewrite `amount_cents` alone,
 * and a gross figure left behind under a higher charge must not pay the owner
 * less than the tenant pays.
 */
export function ownerRentCents(p: KickbackRentRow): number {
  return Math.max(p.gross_amount_cents ?? 0, p.amount_cents ?? 0);
}

/** The part of the owner's rent coliviq funds on this row; 0 when none. */
export function kickbackCents(p: KickbackRentRow): number {
  if (p.gross_amount_cents == null) return 0;
  return Math.max(p.gross_amount_cents - (p.amount_cents ?? 0), 0);
}

/**
 * The `gross_amount_cents` a rent edit writes for a new tenant amount.
 *
 *   - Kickback on and the tenant charged less than the full rent → the full
 *     rent, so the owner keeps earning it.
 *   - Anything else (kickback off, or the new amount is the full rent or more)
 *     → NULL: nothing is discounted, and the owner earns what the tenant pays.
 *
 * `fullRentCents` is the rent before any kickback: the row's own gross figure
 * when it already carries one, else the larger of its amount and the lease
 * rent (see kickbackBaseCents).
 */
export function kickbackGrossCents(
  kickback: boolean,
  newAmountCents: number,
  fullRentCents: number
): number | null {
  return kickback && newAmountCents < fullRentCents ? fullRentCents : null;
}

/**
 * The full rent a rent row's kickback is measured from: its own gross figure
 * when it has one (a kickback or referral credit already on it), else the
 * larger of what it charges and the lease's rent for that month. "Larger"
 * because a row an admin already lowered by hand (the case the kickback
 * checkbox exists to fix after the fact) charges less than the lease.
 */
export function kickbackBaseCents(
  p: KickbackRentRow,
  leaseRentCents: number
): number {
  if (p.gross_amount_cents != null) return p.gross_amount_cents;
  return Math.max(p.amount_cents ?? 0, leaseRentCents);
}
